  (() => {
    if (window.QuadroCartShipping) return;

    const countryRegions = {
      DE: 'DE',
      AT: 'EU',
      BE: 'EU',
      FR: 'EU',
      IT: 'EU',
      LU: 'EU',
      NL: 'EU',
      PL: 'EU',
      ES: 'EU',
      CH: 'CH'
    };

    const tiers = {
      DE: [
        { minimum: 0, rate: 1499 },
        { minimum: 2500, rate: 999 },
        { minimum: 5000, rate: 0 }
      ],
      EU: [
        { minimum: 0, rate: 1999 },
        { minimum: 2500, rate: 1499 },
        { minimum: 5000, rate: 999 },
        { minimum: 10000, rate: 0 }
      ],
      CH: [
        { minimum: 0, rate: 3999 },
        { minimum: 2500, rate: 2999 },
        { minimum: 5000, rate: 2499 },
        { minimum: 40000, rate: 0 }
      ]
    };

    const blockedSlideCountries = new Set(['BE', 'FR', 'LU']);
    const countryStorageKey = 'quadro-cart-shipping-country';

    const cache = new Map();
    let currentCart = null;
    let selectedCountry = '';
    let requestSequence = 0;
    let restrictionState = { blocked: false, country: '' };
    let restrictionSyncFrame = 0;

    const observedCartRoots = new WeakSet();
    const cartRootObservers = [];

    const components = () => Array.from(document.querySelectorAll('[data-quadro-cart-shipping]'));

    const formatMoney = (cents, currency) => {
      if (window.theme && window.theme.Currency && window.theme.moneyFormat) {
        return window.theme.Currency.formatMoney(cents, window.theme.moneyFormatWithCurrency || window.theme.moneyFormat);
      }

      return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
        style: 'currency',
        currency: currency || 'EUR'
      }).format(cents / 100);
    };

    const replaceTokens = (template, replacements) => {
      return Object.keys(replacements).reduce((result, token) => {
        return result.split(token).join(replacements[token]);
      }, template);
    };

    const normalizedText = (value) => String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

    const truthy = (value) => value === true || value === 1 || /^(true|1|yes|ja)$/i.test(String(value || ''));

    const containsSlide = (item) => {
      if (!item) return false;
      const properties = item.properties || {};
      const propertyMatch = Object.keys(properties).some((key) => {
        const normalizedKey = normalizedText(key).replace(/[^a-z0-9]+/g, '_');
        return (normalizedKey === '_contains_slide' || normalizedKey === 'contains_slide') && truthy(properties[key]);
      });
      if (propertyMatch) return true;

      const searchText = normalizedText([
        item.handle,
        item.product_title,
        item.product_type,
        item.variant_title,
        item.sku
      ].join(' '));

      return /\b(rutsche|rutschen|slide|toboggan|tobogan|scivolo|zjezdzalnia|glijbaan)\b/.test(searchText);
    };

    const selectedCountryName = (component) => {
      const select = component.querySelector('[data-quadro-shipping-country]');
      return select.options[select.selectedIndex]?.textContent.trim() || select.value;
    };

    const setCheckoutBlocked = (blocked, message) => {
      document.documentElement.classList.toggle('quadro-slide-shipping-blocked', blocked);
      document.querySelectorAll('[name="checkout"]').forEach((button) => {
        if (blocked) {
          if (!button.dataset.quadroOriginalDisabled) {
            button.dataset.quadroOriginalDisabled = button.disabled ? 'true' : 'false';
          }
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('title', message);
        } else if (button.dataset.quadroOriginalDisabled) {
          button.disabled = button.dataset.quadroOriginalDisabled === 'true';
          if (!button.disabled) button.removeAttribute('aria-disabled');
          button.removeAttribute('title');
          delete button.dataset.quadroOriginalDisabled;
        }
      });
    };

    const markRestrictedItems = (cart, blocked, country, labelTemplate) => {
      const rows = document.querySelectorAll('[data-line], [data-cart-item-index]');
      rows.forEach((row) => {
        const itemKey = row.getAttribute('data-cart-item-key');
        const line = Number(row.getAttribute('data-line') || row.getAttribute('data-cart-item-index'));
        const item = itemKey
          ? cart.items?.find((cartItem) => cartItem.key === itemKey)
          : cart.items?.[line - 1];
        const restricted = blocked && containsSlide(item);
        row.classList.toggle('quadro-cart-line--restricted', restricted);

        let badge = row.querySelector('[data-quadro-slide-badge]');
        if (!badge && restricted) {
          badge = document.createElement('span');
          badge.className = 'quadro-cart-slide-badge';
          badge.dataset.quadroSlideBadge = '';
          const target = row.querySelector('.qcart__details, .eu-cart-page__details, .go-cart-item__info, .cart__product-information > div:last-child');
          if (target) target.appendChild(badge);
        }

        if (badge) {
          if (badge.hidden === restricted) badge.hidden = !restricted;
          if (restricted) {
            const label = replaceTokens(labelTemplate, { __COUNTRY__: country });
            if (badge.textContent !== label) badge.textContent = label;
          }
        }
      });
    };

    const scheduleRestrictedItemSync = () => {
      if (restrictionSyncFrame || !currentCart) return;
      restrictionSyncFrame = window.requestAnimationFrame(() => {
        restrictionSyncFrame = 0;
        const primaryComponent = components()[0];
        if (!primaryComponent) return;
        const country = selectedCountry || primaryComponent.querySelector('[data-quadro-shipping-country]')?.value || '';
        const countryName = selectedCountryName(primaryComponent);
        const badgeTemplate = primaryComponent.dataset.labelSlideItemBadge || '__COUNTRY__';
        const blocked = restrictionState.blocked && restrictionState.country === country;
        markRestrictedItems(currentCart, blocked, countryName, badgeTemplate);
      });
    };

    const observeCartRoots = () => {
      document.querySelectorAll('[data-cart-line-items], [data-quadro-cart-items], .js-go-cart-drawer-content, .js-go-cart-mini-cart-content').forEach((root) => {
        if (observedCartRoots.has(root)) return;
        observedCartRoots.add(root);
        const observer = new MutationObserver(scheduleRestrictedItemSync);
        observer.observe(root, {
          childList: true,
          subtree: true
        });
        cartRootObservers.push(observer);
      });
    };

    const updateSlideRestriction = (cart, country) => {
      const blocked = blockedSlideCountries.has(country)
        && Array.isArray(cart.items)
        && cart.items.some(containsSlide);
      const primaryComponent = components()[0];
      const countryName = primaryComponent ? selectedCountryName(primaryComponent) : country;
      const checkoutMessage = primaryComponent?.dataset.labelSlideCheckoutBlocked || '';
      const badgeTemplate = primaryComponent?.dataset.labelSlideItemBadge || '__COUNTRY__';

      restrictionState = { blocked, country };
      components().forEach((component) => {
        const warning = component.querySelector('[data-quadro-slide-restriction]');
        warning.hidden = !blocked;
        if (blocked) {
          component.querySelector('[data-quadro-slide-restriction-title]').textContent = replaceTokens(
            component.dataset.labelSlideUnavailable,
            { __COUNTRY__: countryName }
          );
          component.querySelector('[data-quadro-slide-restriction-message]').textContent = component.dataset.labelSlideCheckoutBlocked;
        }
      });

      setCheckoutBlocked(blocked, checkoutMessage);
      markRestrictedItems(cart, blocked, countryName, badgeTemplate);
      return blocked;
    };

    const tierState = (country, total, currencyRate = 1) => {
      const region = countryRegions[country];
      const regionTiers = tiers[region];
      if (!regionTiers) return null;

      const convertedTiers = regionTiers.map((tier) => ({
        minimum: Math.round(tier.minimum * currencyRate),
        rate: Math.round(tier.rate * currencyRate)
      }));

      let index = 0;
      convertedTiers.forEach((tier, tierIndex) => {
        if (total >= tier.minimum) index = tierIndex;
      });

      return {
        current: convertedTiers[index],
        next: convertedTiers[index + 1] || null,
        previousMinimum: convertedTiers[index].minimum
      };
    };

    // DEV-1704: use the German domestic label only for the current delivery country.
    // Read the select, not the initial data-country, so country changes stay accurate.
    const isGermanDomesticShipping = (component) => {
      return component.dataset.language === 'de'
        && component.querySelector('[data-quadro-shipping-country]').value === 'DE';
    };

    const freeShippingLabel = (component) => {
      return isGermanDomesticShipping(component)
        ? component.dataset.labelFreeGermany || component.dataset.labelFree
        : component.dataset.labelFree;
    };

    const renderProgress = (component, state, total, currency) => {
      const progress = component.querySelector('[data-quadro-shipping-progress]');
      const progressbar = component.querySelector('[data-quadro-shipping-progressbar]');
      const progressValue = component.querySelector('[data-quadro-shipping-progress-value]');
      const message = component.querySelector('[data-quadro-shipping-message]');

      if (!state) {
        progress.hidden = true;
        return;
      }

      progress.hidden = false;
      let percentage = 100;

      if (state.next) {
        const interval = state.next.minimum - state.previousMinimum;
        const completed = Math.max(0, total - state.previousMinimum);
        percentage = interval > 0 ? Math.min(100, Math.round((completed / interval) * 100)) : 0;
        const amount = formatMoney(Math.max(0, state.next.minimum - total), currency);
        const price = state.next.rate === 0 ? freeShippingLabel(component) : formatMoney(state.next.rate, currency);
        message.textContent = replaceTokens(component.dataset.labelNextTier, {
          __AMOUNT__: amount,
          __PRICE__: price
        });
      } else {
        message.textContent = component.dataset.labelFreeReached;
      }

      progressbar.setAttribute('aria-valuenow', String(percentage));
      progressValue.style.width = `${percentage}%`;
    };

    const renderRate = (component, cart, rates, fallbackOnly, slideBlocked) => {
      const country = component.querySelector('[data-quadro-shipping-country]').value;
      const currency = cart.currency || component.dataset.currency || 'EUR';
      const shopCurrency = component.dataset.shopCurrency || currency;
      const presentmentRate = currency === shopCurrency
        ? 1
        : Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
      const state = Number.isFinite(presentmentRate) && presentmentRate > 0
        ? tierState(country, cart.total_price, presentmentRate)
        : null;
      const rateElement = component.querySelector('[data-quadro-shipping-rate]');

      component.hidden = cart.item_count === 0 || cart.requires_shipping === false;
      if (component.hidden) return;

      if (slideBlocked) {
        rateElement.textContent = component.dataset.labelSlideRateUnavailable;
        component.dataset.rateSource = 'slide-restriction';
        renderProgress(component, null, cart.total_price, currency);
        return;
      }

      if (Array.isArray(rates) && rates.length === 0) {
        rateElement.textContent = component.dataset.labelUnavailable;
        renderProgress(component, null, cart.total_price, currency);
        return;
      }

      if (Array.isArray(rates) && rates.length > 0) {
        const sortedRates = rates.slice().sort((left, right) => Number(left.price) - Number(right.price));
        const price = Math.round(Number(sortedRates[0].price) * 100);
        const formattedPrice = price === 0 ? freeShippingLabel(component) : formatMoney(price, currency);
        // Keep the exact DE wording even when Shopify returns several shipping methods.
        const showFromPrefix = sortedRates.length > 1 && !(price === 0 && isGermanDomesticShipping(component));
        rateElement.textContent = showFromPrefix
          ? replaceTokens(component.dataset.labelMultipleRates, { __PRICE__: formattedPrice })
          : formattedPrice;
        component.dataset.rateCount = String(sortedRates.length);
        component.dataset.rateSource = 'shopify';
      } else if (state) {
        const formattedPrice = state.current.rate === 0
          ? freeShippingLabel(component)
          : formatMoney(state.current.rate, currency);
        rateElement.textContent = fallbackOnly
          ? formattedPrice
          : component.dataset.labelCalculating;
        component.dataset.rateSource = fallbackOnly ? 'tier-config' : 'loading';
      } else {
        rateElement.textContent = component.dataset.labelUnavailable;
        component.dataset.rateSource = 'unavailable';
      }

      renderProgress(component, state, cart.total_price, currency);
    };

    const shippingRatesUrl = (component, endpoint, country) => {
      const root = component.dataset.rootUrl || '/';
      const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
      const query = new URLSearchParams();
      query.set('shipping_address[country]', country);
      return `${normalizedRoot}cart/${endpoint}.json?${query.toString()}`;
    };

    const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

    const loadRates = async (component, country) => {
      const prepareUrl = shippingRatesUrl(component, 'prepare_shipping_rates', country);
      const asyncUrl = shippingRatesUrl(component, 'async_shipping_rates', country);
      const prepareResponse = await fetch(prepareUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if (!prepareResponse.ok) throw new Error(`Shipping rate preparation failed: ${prepareResponse.status}`);

      for (let attempt = 0; attempt < 8; attempt += 1) {
        if (attempt > 0) await wait(350);
        const response = await fetch(asyncUrl, {
          method: 'GET',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) throw new Error(`Shipping rate request failed: ${response.status}`);
        const result = await response.json();
        if (result && Array.isArray(result.shipping_rates)) return result.shipping_rates;
      }

      throw new Error('Shipping rate calculation timed out');
    };

    const refresh = async (cart) => {
      const sequence = ++requestSequence;
      currentCart = cart;
      const activeComponents = components();
      if (activeComponents.length === 0) return;

      const country = activeComponents[0].querySelector('[data-quadro-shipping-country]').value;
      const slideBlocked = updateSlideRestriction(cart, country);
      activeComponents.forEach((component) => renderRate(component, cart, null, false, slideBlocked));
      if (cart.item_count === 0 || cart.requires_shipping === false) return;
      if (slideBlocked) return;

      const cacheKey = JSON.stringify([country, cart.token || '', cart.currency, cart.total_price, (cart.items || []).map((item) => [item.key, item.quantity, item.final_line_price, item.requires_shipping])]);

      if (cache.has(cacheKey)) {
        activeComponents.forEach((component) => renderRate(component, cart, cache.get(cacheKey), false, false));
        return;
      }

      try {
        const rates = await loadRates(activeComponents[0], country);
        if (sequence !== requestSequence) return;
        cache.set(cacheKey, rates);
        activeComponents.forEach((component) => renderRate(component, cart, rates, false, false));
      } catch (error) {
        if (sequence !== requestSequence) return;
        activeComponents.forEach((component) => renderRate(component, cart, null, true, false));
        console.warn('[QUADRO shipping calculator]', error);
      }
    };

    const syncCountry = (country) => {
      let available = false;
      components().forEach((component) => {
        const select = component.querySelector('[data-quadro-shipping-country]');
        if (!select || !Array.from(select.options).some((option) => option.value === country)) return;
        select.value = country;
        select.disabled = false;
        component.querySelector('[data-quadro-shipping-rate]').textContent = component.dataset.labelCalculating;
        available = true;
      });

      if (available) selectedCountry = country;
      return available;
    };

    const storedCountry = () => {
      try {
        return window.localStorage.getItem(countryStorageKey) || '';
      } catch (error) {
        return '';
      }
    };

    const publishCountry = (country) => {
      if (!syncCountry(country)) return;

      try {
        window.localStorage.setItem(countryStorageKey, country);
      } catch (error) {}

      if (currentCart) refresh(currentCart);
    };

    const bind = () => {
      observeCartRoots();
      components().forEach((component) => {
        if (component.dataset.quadroShippingBound === 'true') return;
        component.dataset.quadroShippingBound = 'true';
        const select = component.querySelector('[data-quadro-shipping-country]');
        select.addEventListener('change', () => publishCountry(select.value));
      });
    };

    const initialize = () => {
      const firstSelect = components()[0]?.querySelector('[data-quadro-shipping-country]');
      const initialCountry = storedCountry() || firstSelect?.value || '';
      if (initialCountry) syncCountry(initialCountry);
      bind();
      const pageState = document.querySelector('[data-quadro-cart-page-state]');
      if (pageState) {
        try { refresh(JSON.parse(pageState.textContent)); } catch (error) {}
      }
    };

    document.addEventListener('quadro:cart-updated', (event) => {
      bind();
      if (selectedCountry) syncCountry(selectedCountry);
      if (event.detail && event.detail.cart) refresh(event.detail.cart);
    });

    document.addEventListener('shopify:section:load', () => {
      bind();
      if (selectedCountry) syncCountry(selectedCountry);
      if (currentCart) refresh(currentCart);
    });

    document.addEventListener('submit', (event) => {
      if (!restrictionState.blocked || !event.target.matches('[data-quadro-cart-page-form]')) return;
      if (event.submitter?.name !== 'checkout') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      document.querySelector('[data-quadro-slide-restriction]:not([hidden])')?.focus();
    }, true);

    document.addEventListener('click', (event) => {
      if (!restrictionState.blocked) return;
      const checkoutTarget = event.target.closest('[name="checkout"], a[href*="/checkout"]');
      if (!checkoutTarget) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const warning = document.querySelector('[data-quadro-slide-restriction]:not([hidden])');
      if (warning) {
        warning.focus();
        warning.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, true);

    window.QuadroCartShipping = { refresh, tierState, containsSlide };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialize, { once: true });
    } else {
      initialize();
    }
  })();
